/**
 * Xero Connect Flow Component
 *
 * Feature: F006 - Xero Connect Flow UI (Sprint 24)
 *
 * Manages the Xero OAuth connection within the Add Financial Year wizard.
 * Handles four states:
 *   loading    → spinner while checking connections
 *   no-connection → "Connect to Xero" with OAuth initiation
 *   connected  → org name, status badge, "Use This Connection"
 *   error      → token_expired / sync_error with reconnect option
 *
 * Used within the financial year wizard when "Xero Sync" method is selected.
 */

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Loader2, AlertCircle, CheckCircle2, RefreshCw, Link2Off } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { apiRequest, getAuthHeaders } from '@/lib/queryClient';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface XeroConnection {
  id: string;
  accountId: string;
  xeroTenantId: string;
  xeroOrgName: string;
  syncStatus: 'connected' | 'syncing' | 'sync_error' | 'disconnected' | 'token_expired';
  lastSyncAt: string | null;
  lastSyncError?: string | null;
  createdAt: string;
}

export interface XeroConnectFlowProps {
  accountId: string;
  onConnectionReady: (connectionId: string, orgName: string) => void;
  onBack: () => void;
  onCancel: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isErrorStatus(
  status: XeroConnection['syncStatus'],
): status is 'token_expired' | 'sync_error' {
  return status === 'token_expired' || status === 'sync_error';
}

function formatSyncDate(lastSyncAt: string | null): string {
  if (!lastSyncAt) return 'Never synced';
  return new Date(lastSyncAt).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function syncStatusLabel(status: XeroConnection['syncStatus']): string {
  switch (status) {
    case 'connected':
      return 'Connected';
    case 'syncing':
      return 'Syncing';
    case 'sync_error':
      return 'Sync Error';
    case 'disconnected':
      return 'Disconnected';
    case 'token_expired':
      return 'Token Expired';
    default:
      return status;
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function XeroConnectFlow({
  accountId,
  onConnectionReady,
  onBack,
  onCancel,
}: XeroConnectFlowProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [disconnectTarget, setDisconnectTarget] = useState<XeroConnection | null>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [selectedConnectionId, setSelectedConnectionId] = useState<string | null>(null);

  // -------------------------------------------------------------------------
  // Fetch connections
  // -------------------------------------------------------------------------

  const {
    data: connections = [],
    isLoading,
    isError,
    error,
  } = useQuery<XeroConnection[]>({
    queryKey: ['/api/xero/connections', { accountId }],
    enabled: !!accountId,
  });

  // Auto-select when single connection exists
  useEffect(() => {
    if (connections.length === 1) {
      setSelectedConnectionId(connections[0].id);
    } else {
      setSelectedConnectionId(null);
    }
  }, [connections]);

  // -------------------------------------------------------------------------
  // Disconnect mutation
  // -------------------------------------------------------------------------

  const disconnectMutation = useMutation({
    mutationFn: async (connectionId: string) => {
      await apiRequest('DELETE', `/api/xero/connections/${connectionId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/xero/connections'] });
      toast({ title: 'Xero disconnected successfully' });
      setDisconnectTarget(null);
    },
    onError: (err: Error) => {
      toast({
        title: 'Disconnect failed',
        description: err.message,
        variant: 'destructive',
      });
      setDisconnectTarget(null);
    },
  });

  // -------------------------------------------------------------------------
  // OAuth initiation
  // -------------------------------------------------------------------------

  const initiateOAuth = async () => {
    try {
      setIsRedirecting(true);
      const authHeaders = await getAuthHeaders();
      const res = await fetch(`/api/xero/auth-url?accountId=${encodeURIComponent(accountId)}`, {
        headers: authHeaders,
        credentials: 'include',
      });
      if (!res.ok) {
        throw new Error(`Failed to get auth URL: ${res.statusText}`);
      }
      const { authUrl } = await res.json();
      window.location.href = authUrl;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to initiate Xero connection';
      setIsRedirecting(false);
      toast({ title: 'Connection failed', description: message, variant: 'destructive' });
    }
  };

  // -------------------------------------------------------------------------
  // State: Loading
  // -------------------------------------------------------------------------

  if (isLoading) {
    return (
      <div className="space-y-4 py-4" data-testid="xero-connect-flow">
        <div className="flex items-center justify-center gap-3 py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Checking Xero connections...</span>
        </div>
        <NavigationFooter onBack={onBack} onCancel={onCancel} />
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // State: Network error
  // -------------------------------------------------------------------------

  if (isError) {
    return (
      <div className="space-y-4 py-4" data-testid="xero-connect-flow">
        <div className="flex flex-col items-center justify-center gap-3 py-6 text-center">
          <AlertCircle className="h-8 w-8 text-destructive" />
          <p className="text-sm text-destructive font-medium">Failed to load Xero connections</p>
          <p className="text-xs text-muted-foreground">
            {error instanceof Error ? error.message : 'Network error. Please try again.'}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => queryClient.invalidateQueries({ queryKey: ['/api/xero/connections'] })}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </div>
        <NavigationFooter onBack={onBack} onCancel={onCancel} />
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // State: Redirecting to Xero
  // -------------------------------------------------------------------------

  if (isRedirecting) {
    return (
      <div className="space-y-4 py-4" data-testid="xero-connect-flow">
        <div className="flex flex-col items-center justify-center gap-3 py-8 text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          <p className="text-sm font-medium">Redirecting to Xero...</p>
          <button
            type="button"
            className="text-xs text-muted-foreground underline hover:text-foreground"
            onClick={() => setIsRedirecting(false)}
          >
            Taking too long? Click to retry
          </button>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // State: No connections
  // -------------------------------------------------------------------------

  if (connections.length === 0) {
    return (
      <div className="space-y-4" data-testid="xero-connect-flow">
        <div className="text-center space-y-3 py-4">
          {/* Xero brand icon placeholder */}
          <div className="mx-auto h-12 w-12 rounded-full bg-sky-100 flex items-center justify-center">
            <CheckCircle2 className="h-6 w-6 text-sky-600" />
          </div>
          <h3 className="text-lg font-semibold">Connect to Xero</h3>
          <p className="text-sm text-muted-foreground max-w-sm mx-auto">
            Link your Xero organisation to pull financial data directly
          </p>
        </div>

        {/* Info callout */}
        <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-800">
          You will be redirected to Xero to authorise access. We only request{' '}
          <strong>read-only</strong> access to reports and settings.
        </div>

        <Button
          className="w-full"
          onClick={initiateOAuth}
          data-testid="button-connect-xero"
        >
          Connect Xero Account
        </Button>

        <NavigationFooter onBack={onBack} onCancel={onCancel} />
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // State: One or more connections (connected or error)
  // -------------------------------------------------------------------------

  // Determine if any connection has an error status
  const hasError = connections.some((c) => isErrorStatus(c.syncStatus));
  const heading = hasError ? 'Xero Connection Issue' : 'Xero Connected';

  // For multi-connection: the selected connection (or first for single)
  const activeConnection =
    connections.find((c) => c.id === selectedConnectionId) ?? connections[0];

  const handleUseConnection = () => {
    if (!activeConnection) return;
    onConnectionReady(activeConnection.id, activeConnection.xeroOrgName);
  };

  return (
    <div className="space-y-4" data-testid="xero-connect-flow">
      <h3 className="text-lg font-semibold">{heading}</h3>

      {/* Connection list */}
      <div className="space-y-2">
        {connections.map((connection) => {
          const isSelected = connection.id === selectedConnectionId;
          const hasConnError = isErrorStatus(connection.syncStatus);

          return (
            <Card
              key={connection.id}
              className={[
                'cursor-pointer border transition-colors',
                isSelected ? 'border-primary' : 'border-border',
                connections.length > 1 ? 'hover:border-primary' : '',
              ]
                .join(' ')
                .trim()}
              onClick={() => connections.length > 1 && setSelectedConnectionId(connection.id)}
              data-testid={`connection-card-${connection.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="space-y-1 min-w-0">
                    {/* Org name */}
                    <p className="text-sm font-medium truncate">{connection.xeroOrgName}</p>

                    {/* Sync date */}
                    <p className="text-xs text-muted-foreground">
                      Last sync: {formatSyncDate(connection.lastSyncAt)}
                    </p>

                    {/* Error message */}
                    {hasConnError && connection.lastSyncError && (
                      <p className="text-xs text-destructive">{connection.lastSyncError}</p>
                    )}
                  </div>

                  {/* Status badge */}
                  <Badge
                    variant={
                      connection.syncStatus === 'connected'
                        ? 'default'
                        : hasConnError
                          ? 'destructive'
                          : 'outline'
                    }
                    data-testid="badge-connection-status"
                  >
                    {syncStatusLabel(connection.syncStatus)}
                  </Badge>
                </div>

                {/* Disconnect link */}
                <div className="mt-3 pt-3 border-t flex items-center justify-end">
                  <button
                    type="button"
                    className="text-xs text-destructive hover:underline"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDisconnectTarget(connection);
                    }}
                    data-testid="button-disconnect-xero"
                  >
                    <Link2Off className="h-3 w-3 inline mr-1" />
                    Disconnect
                  </button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Action buttons */}
      <div className="space-y-2">
        {hasError ? (
          <Button
            className="w-full"
            onClick={initiateOAuth}
            data-testid="button-reconnect-xero"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Reconnect
          </Button>
        ) : (
          <Button
            className="w-full"
            onClick={handleUseConnection}
            disabled={!selectedConnectionId}
            data-testid="button-use-connection"
          >
            {connections.length > 1 ? 'Use Selected Connection' : 'Use This Connection'}
          </Button>
        )}
      </div>

      <NavigationFooter onBack={onBack} onCancel={onCancel} />

      {/* Disconnect confirmation dialog */}
      <AlertDialog
        open={!!disconnectTarget}
        onOpenChange={(open) => {
          if (!open) setDisconnectTarget(null);
        }}
      >
        <AlertDialogContent data-testid="dialog-disconnect-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect Xero?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the connection to{' '}
              <strong>{disconnectTarget?.xeroOrgName}</strong>. You can reconnect at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button
              variant="outline"
              onClick={() => setDisconnectTarget(null)}
              data-testid="button-cancel-disconnect"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (disconnectTarget) {
                  disconnectMutation.mutate(disconnectTarget.id);
                }
              }}
              disabled={disconnectMutation.isPending}
              data-testid="button-confirm-disconnect"
            >
              {disconnectMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Disconnect
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Navigation Footer (shared sub-component)
// ---------------------------------------------------------------------------

interface NavigationFooterProps {
  onBack: () => void;
  onCancel: () => void;
}

function NavigationFooter({ onBack, onCancel }: NavigationFooterProps) {
  return (
    <div className="flex justify-between pt-2">
      <Button variant="outline" onClick={onBack} data-testid="button-wizard-back">
        Back
      </Button>
      <Button variant="ghost" onClick={onCancel} data-testid="button-wizard-cancel">
        Cancel
      </Button>
    </div>
  );
}
